import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, Package, Users, LogOut, MessageSquare, Search, FileText, BarChart3, Shield, Menu, X, KeyRound, ClipboardList, FileCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useCallback } from "react";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { toast } from "sonner";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastNotifiedCount, setLastNotifiedCount] = useState(0);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Poll for new messages
  const checkUnread = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/messages/unread-count`);
      const count = response.data.unread_count;
      setUnreadCount(count);

      // Show popup notification if new messages arrived and we're not on messages page
      if (count > lastNotifiedCount && lastNotifiedCount >= 0 && location.pathname !== "/messages") {
        const newMsgs = count - lastNotifiedCount;
        if (newMsgs > 0 && lastNotifiedCount > 0) {
          toast.info(`${newMsgs} new message${newMsgs > 1 ? "s" : ""} from Admin`, { duration: 5000 });
        }
      }
      setLastNotifiedCount(count);
    } catch (error) {
      console.error("Unread check failed:", error.message);
    }
  }, [lastNotifiedCount, location.pathname]);

  useEffect(() => {
    checkUnread();
    const interval = setInterval(checkUnread, 15000);
    return () => clearInterval(interval);
  }, []);

  // Reset unread when visiting messages page
  useEffect(() => {
    if (location.pathname === "/messages") {
      setUnreadCount(0);
      setLastNotifiedCount(0);
    }
  }, [location.pathname]);

  const navItems = [
    { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
    { name: "Inventory", path: "/inventory", icon: Package },
    { name: "Search & Browse", path: "/search", icon: Search },
    { name: "Purchase Orders", path: "/purchase-orders", icon: FileText },
    { name: "Stock Ledger", path: "/stock-ledger", icon: ClipboardList },
    { name: "Quotations", path: "/quotations", icon: FileCheck },
    { name: "Reports", path: "/reports", icon: BarChart3 },
    { name: "Messages", path: "/messages", icon: MessageSquare, badge: unreadCount },
  ];

  if (user?.role === "admin") {
    navItems.push({ name: "Security", path: "/security", icon: Shield });
    navItems.push({ name: "Users", path: "/users", icon: Users });
  }

  const NavLink = ({ item, onClick }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={onClick}
        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
        className={`flex items-center gap-3 px-4 py-3 rounded-sm mb-1 transition-colors duration-100 relative ${
          isActive
            ? "bg-orange-500 text-white"
            : "text-slate-300 hover:bg-slate-800 hover:text-white"
        }`}
      >
        <Icon size={20} strokeWidth={2} />
        <span className="font-medium">{item.name}</span>
        {item.badge > 0 && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 min-w-[20px] h-5 flex items-center justify-center bg-orange-500 text-white text-xs font-bold rounded-full px-1.5" data-testid="unread-badge">
            {item.badge > 99 ? "99+" : item.badge}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center justify-between">
        <h1 className="text-lg font-black text-white uppercase tracking-tight">DOZERWORLD</h1>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Link to="/messages" className="relative p-2">
              <MessageSquare size={20} className="text-white" />
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center bg-orange-500 text-white text-xs font-bold rounded-full px-1">{unreadCount}</span>
            </Link>
          )}
          <Button variant="ghost" size="sm" className="text-white hover:bg-slate-800" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileMenuOpen(false)} />}

      {/* Sidebar */}
      <aside className={`w-64 bg-slate-900 text-slate-300 border-r border-slate-800 fixed h-screen z-50 transform transition-transform duration-200 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 flex flex-col`}>
        <div className="p-6 border-b border-slate-800 shrink-0">
          <h1 className="text-xl font-black text-white uppercase tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>DOZERWORLD</h1>
          <p className="text-xs text-slate-400 mt-1">Inventory System</p>
        </div>
        <nav className="p-4 overflow-y-auto flex-1 min-h-0">
          {navItems.map((item) => (
            <NavLink key={item.path} item={item} onClick={() => setMobileMenuOpen(false)} />
          ))}
        </nav>
        <div className="shrink-0 p-4 border-t border-slate-800 bg-slate-900">
          <div className="mb-3 px-2">
            <p className="text-xs text-slate-400 uppercase tracking-wider" style={{ fontFamily: 'Chivo, sans-serif' }}>Logged in as</p>
            <p className="text-sm text-white font-medium mt-1 truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            <span className="inline-block mt-2 px-2 py-1 bg-slate-800 text-orange-400 text-xs font-bold uppercase rounded-sm">{user?.role}</span>
          </div>
          <Button onClick={() => setChangePasswordOpen(true)} data-testid="change-password-button" variant="ghost" className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white rounded-sm btn-snap mb-1">
            <KeyRound size={20} className="mr-3" strokeWidth={2} /> Change Password
          </Button>
          <Button onClick={handleLogout} data-testid="logout-button" variant="ghost" className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white rounded-sm btn-snap">
            <LogOut size={20} className="mr-3" strokeWidth={2} /> Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0">
        <Outlet />
      </main>

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </div>
  );
};

export default DashboardLayout;
